import React, { useState, useEffect, useCallback } from "react";
import styled, { useTheme } from "styled-components";
import { apiFetch } from "../../services/apiHelper";
import { API_ENDPOINTS } from "../../services/Constants";

const PageWrapper = styled.div`
    max-width: 1100px;
    margin: 2rem auto;
    padding: 0 1rem;
`;

const PageTitle = styled.h2`
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 1.5rem;
    font-size: 1.6rem;
`;

const TabBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    border-bottom: 2px solid ${({ theme }) => theme.colors.lineColor};
    margin-bottom: 1.5rem;
`;

const Tab = styled.button`
    padding: 0.55rem 1.1rem;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.text};
    box-shadow: ${({ $active, theme }) => $active ? `inset 0 -2px 0 ${theme.colors.primary}` : "none"};
    white-space: nowrap;
    transition: color 0.15s, box-shadow 0.15s;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const BadgeCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    border-radius: 9px;
    font-size: 0.72rem;
    background: ${({ theme }) => theme.status.error};
    color: #fff;
`;

const Card = styled.div`
    background: ${({ theme }) => theme.colors.card};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    box-shadow: ${({ theme }) => theme.shadows.md};
    padding: 1.5rem;
    overflow-x: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
`;

const Th = styled.th`
    text-align: left;
    padding: 0.6rem 0.8rem;
    color: ${({ theme }) => theme.colors.lineColor};
    font-weight: 600;
    border-bottom: 2px solid ${({ theme }) => theme.colors.lineColor};
    white-space: nowrap;
`;

const Tr = styled.tr`
    &:not(:last-child) td { border-bottom: 1px solid ${({ theme }) => theme.colors.lineColor}33; }
    background: ${({ $overdue, theme }) => $overdue ? theme.status.error + "11" : "transparent"};
`;

const Td = styled.td`
    padding: 0.6rem 0.8rem;
    color: ${({ theme }) => theme.colors.text};
    vertical-align: middle;
`;

const Sub = styled.span`
    color: ${({ theme }) => theme.colors.lineColor};
    font-size: 0.82rem;
`;

const StatusBadge = styled.span`
    display: inline-block;
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
    font-size: 0.78rem;
    font-weight: 600;
    background: ${({ $s, theme }) =>
        $s === "ACTIVE"    ? theme.status.success + "22" :
        $s === "EXPIRED"   ? theme.status.error + "22" :
        $s === "CANCELLED" ? theme.colors.lineColor + "33" :
        theme.status.warning + "22"};
    color: ${({ $s, theme }) =>
        $s === "ACTIVE"    ? theme.status.success :
        $s === "EXPIRED"   ? theme.status.error :
        $s === "CANCELLED" ? theme.colors.lineColor :
        theme.status.warning};
`;

const OverduePill = styled.span`
    display: inline-block;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
    font-size: 0.72rem;
    font-weight: 600;
    background: ${({ theme }) => theme.status.error + "22"};
    color: ${({ theme }) => theme.status.error};
    margin-left: 0.35rem;
`;

const EmptyMsg = styled.p`
    text-align: center;
    color: ${({ theme }) => theme.colors.lineColor};
    padding: 2rem 0;
    margin: 0;
`;

function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("hr-HR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function UserCell({ item }) {
    return (
        <Td>
            {item.userFirstName} {item.userLastName}{" "}
            <Sub>({item.username})</Sub>
        </Td>
    );
}

const PAGE_SIZE = 20;

const Pagination = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1rem;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.text};
`;

const PageBtn = styled.button`
    padding: 0.35rem 0.7rem;
    border: 1px solid ${({ theme }) => theme.colors.lineColor};
    border-radius: 4px;
    background: none;
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    &:disabled { opacity: 0.4; cursor: default; }
    &:not(:disabled):hover { border-color: ${({ theme }) => theme.colors.primary}; color: ${({ theme }) => theme.colors.primary}; }
`;

function usePagedTab(endpoint, page) {
    const [data, setData] = useState({ content: [], totalElements: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiFetch(`${endpoint}?page=${page}&size=${PAGE_SIZE}`, { method: "GET" });
            if (!res.ok) throw new Error("Greška pri učitavanju");
            setData(await res.json());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [endpoint, page]);

    useEffect(() => { load(); }, [load]);

    return { content: data.content || [], totalElements: data.totalElements || 0, loading, error };
}

function TabPages({ totalElements, page, setPage }) {
    const totalPages = Math.ceil(totalElements / PAGE_SIZE);
    if (totalPages <= 1) return null;
    return (
        <Pagination>
            <PageBtn disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹</PageBtn>
            <span>{page + 1} / {totalPages}</span>
            <PageBtn disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>›</PageBtn>
        </Pagination>
    );
}

const TABS = [
    { id: "active",       label: "Aktivne posudbe" },
    { id: "overdue",      label: "Prekoračenja" },
    { id: "reservations", label: "Rezervacije" },
    { id: "returned",     label: "Vraćene knjige" },
];

function BorrowingManagement() {
    const [activeTab, setActiveTab] = useState("active");
    const [activePage, setActivePage]             = useState(0);
    const [overduePage, setOverduePage]           = useState(0);
    const [reservationsPage, setReservationsPage] = useState(0);
    const [returnedPage, setReturnedPage]         = useState(0);
    const theme = useTheme();

    const active       = usePagedTab(API_ENDPOINTS.BORROWING_ALL_ACTIVE,       activePage);
    const overdue      = usePagedTab(API_ENDPOINTS.BORROWING_ALL_OVERDUE,      overduePage);
    const reservations = usePagedTab(API_ENDPOINTS.BORROWING_ALL_RESERVATIONS, reservationsPage);
    const returned     = usePagedTab(API_ENDPOINTS.BORROWING_ALL_RETURNED,     returnedPage);

    const globalError = active.error || overdue.error || reservations.error || returned.error;
    if (globalError) return <PageWrapper><p style={{ color: theme.status.error }}>{globalError}</p></PageWrapper>;

    return (
        <PageWrapper>
            <PageTitle>Pregled posudbi</PageTitle>

            <TabBar>
                {TABS.map(t => (
                    <Tab key={t.id} $active={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
                        {t.label}
                        {t.id === "overdue" && overdue.totalElements > 0 && (
                            <BadgeCount>{overdue.totalElements}</BadgeCount>
                        )}
                    </Tab>
                ))}
            </TabBar>

            {activeTab === "active" && (
                <Card>
                    {active.loading ? <EmptyMsg>Učitavanje...</EmptyMsg> : active.content.length === 0 ? <EmptyMsg>Nema aktivnih posudbi</EmptyMsg> : (
                        <Table>
                            <thead><tr>
                                <Th>Knjiga</Th><Th>Korisnik</Th>
                                <Th>Datum posudbe</Th><Th>Rok vraćanja</Th>
                            </tr></thead>
                            <tbody>
                                {active.content.map((b, i) => (
                                    <Tr key={i} $overdue={b.overdue}>
                                        <Td>{b.bookName}</Td>
                                        <UserCell item={b} />
                                        <Td>{formatDate(b.borrowedDate)}</Td>
                                        <Td>
                                            {formatDate(b.returnDate)}
                                            {b.overdue && <OverduePill>kasni</OverduePill>}
                                        </Td>
                                    </Tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    <TabPages totalElements={active.totalElements} page={activePage} setPage={setActivePage} />
                </Card>
            )}

            {activeTab === "overdue" && (
                <Card>
                    {overdue.loading ? <EmptyMsg>Učitavanje...</EmptyMsg> : overdue.content.length === 0 ? <EmptyMsg>Nema prekoračenja</EmptyMsg> : (
                        <Table>
                            <thead><tr>
                                <Th>Knjiga</Th><Th>Korisnik</Th>
                                <Th>Datum posudbe</Th><Th>Rok vraćanja</Th>
                            </tr></thead>
                            <tbody>
                                {overdue.content.map((b, i) => (
                                    <Tr key={i} $overdue>
                                        <Td>{b.bookName}</Td>
                                        <UserCell item={b} />
                                        <Td>{formatDate(b.borrowedDate)}</Td>
                                        <Td>{formatDate(b.returnDate)}</Td>
                                    </Tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    <TabPages totalElements={overdue.totalElements} page={overduePage} setPage={setOverduePage} />
                </Card>
            )}

            {activeTab === "reservations" && (
                <Card>
                    {reservations.loading ? <EmptyMsg>Učitavanje...</EmptyMsg> : reservations.content.length === 0 ? <EmptyMsg>Nema aktivnih rezervacija</EmptyMsg> : (
                        <Table>
                            <thead><tr>
                                <Th>Knjiga</Th><Th>Korisnik</Th>
                                <Th>Datum rezervacije</Th><Th>Status</Th>
                            </tr></thead>
                            <tbody>
                                {reservations.content.map((r, i) => (
                                    <Tr key={i}>
                                        <Td>{r.bookName}</Td>
                                        <UserCell item={r} />
                                        <Td>{formatDate(r.reservationDate)}</Td>
                                        <Td><StatusBadge $s={r.status}>{r.status}</StatusBadge></Td>
                                    </Tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    <TabPages totalElements={reservations.totalElements} page={reservationsPage} setPage={setReservationsPage} />
                </Card>
            )}

            {activeTab === "returned" && (
                <Card>
                    {returned.loading ? <EmptyMsg>Učitavanje...</EmptyMsg> : returned.content.length === 0 ? <EmptyMsg>Nema vraćenih knjiga</EmptyMsg> : (
                        <Table>
                            <thead><tr>
                                <Th>Knjiga</Th><Th>Korisnik</Th>
                                <Th>Datum posudbe</Th><Th>Vraćeno</Th>
                            </tr></thead>
                            <tbody>
                                {returned.content.map((b, i) => (
                                    <Tr key={i}>
                                        <Td>{b.bookName}</Td>
                                        <UserCell item={b} />
                                        <Td>{formatDate(b.borrowedDate)}</Td>
                                        <Td>{formatDate(b.returnedDate)}</Td>
                                    </Tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    <TabPages totalElements={returned.totalElements} page={returnedPage} setPage={setReturnedPage} />
                </Card>
            )}
        </PageWrapper>
    );
}

export default BorrowingManagement;
